import { z } from 'zod'
import {
  CreateJobRequestSchema,
  JobSchema,
  JobEventSchema,
  ErrorResponseSchema,
  MemoryListResponseSchema,
  MemoryDecisionResponseSchema,
  type CreateJobRequest,
  type Job,
  type JobEvent,
  type Memory,
  type MemoryStatus,
  type MemoryListResponse,
  type MemoryDecisionResponse,
} from '@agent/shared'

// ISSUE #10: API client。Cloudflare Access SSO Cookie を自動で送るために
// `credentials: 'include'` を必須にする。dev では Vite proxy 経由なので same-origin、
// production では Custom Domain 配下の `/api/*` を叩く前提で相対 URL を使う。
//
// Worker の実装 (packages/worker/src/routes/jobs.ts) と一致させたレスポンス schema:
//   GET  /api/jobs       -> { jobs: Job[] }                 (cursor は job.id を caller で持つ)
//   GET  /api/jobs/:id   -> Job & { events: JobEvent[] }    (job がフラット展開されている)
//   POST /api/jobs       -> { id: string, status: 'created' } (201)

const JobListResponseSchema = z.object({
  jobs: z.array(JobSchema),
})
export type JobListResponse = z.infer<typeof JobListResponseSchema>

const JobDetailResponseSchema = JobSchema.extend({
  events: z.array(JobEventSchema),
})
export type JobDetailResponse = z.infer<typeof JobDetailResponseSchema>

const CreateJobResponseSchema = z.object({
  id: z.string().uuid(),
  status: z.literal('created'),
})
export type CreateJobResponse = z.infer<typeof CreateJobResponseSchema>

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: unknown
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message)
    this.status = status
    this.code = code
    this.details = details
  }
}

async function request<T>(
  path: string,
  init: RequestInit,
  schema: z.ZodType<T>,
): Promise<T> {
  let res: Response
  try {
    res = await fetch(path, {
      ...init,
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
        accept: 'application/json',
        ...(init.headers ?? {}),
      },
    })
  } catch (e) {
    throw new ApiError(0, 'network_error', `Network error: ${(e as Error).message}`)
  }

  const text = await res.text()
  let body: unknown = null
  if (text.length > 0) {
    try {
      body = JSON.parse(text)
    } catch {
      throw new ApiError(res.status, 'invalid_json', `Non-JSON response (status ${res.status})`)
    }
  }

  if (!res.ok) {
    const parsed = ErrorResponseSchema.safeParse(body)
    if (parsed.success) {
      throw new ApiError(res.status, parsed.data.error, parsed.data.error, parsed.data.details)
    }
    throw new ApiError(res.status, 'unknown_error', `Request failed (status ${res.status})`)
  }

  const parsed = schema.safeParse(body)
  if (!parsed.success) {
    throw new ApiError(res.status, 'schema_mismatch', 'Response schema mismatch', parsed.error.format())
  }
  return parsed.data
}

export type ListJobsParams = {
  limit?: number
  before?: string
  project?: string
}

function buildQuery(params: Record<string, unknown>): string {
  const usp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue
    usp.set(k, String(v))
  }
  const s = usp.toString()
  return s.length > 0 ? `?${s}` : ''
}

export const api = {
  listJobs(params: ListJobsParams = {}): Promise<JobListResponse> {
    return request(
      `/api/jobs${buildQuery(params as Record<string, unknown>)}`,
      { method: 'GET' },
      JobListResponseSchema,
    )
  },

  getJob(id: string): Promise<JobDetailResponse> {
    return request(
      `/api/jobs/${encodeURIComponent(id)}`,
      { method: 'GET' },
      JobDetailResponseSchema,
    )
  },

  createJob(body: CreateJobRequest): Promise<CreateJobResponse> {
    // クライアント側でも一度バリデーション (悪意ではなく typo 防止のため)
    const parsed = CreateJobRequestSchema.safeParse(body)
    if (!parsed.success) {
      return Promise.reject(
        new ApiError(0, 'invalid_request', 'Invalid request body', parsed.error.format()),
      )
    }
    return request(
      '/api/jobs',
      { method: 'POST', body: JSON.stringify(parsed.data) },
      CreateJobResponseSchema,
    )
  },

  // ISSUE #23: Memory Manager UI 用。
  //   GET /api/memories?status=candidate&project=&limit=50
  //   POST /api/memories/:id/approve
  //   POST /api/memories/:id/reject
  listMemories(params: ListMemoriesParams): Promise<MemoryListResponse> {
    return request(
      `/api/memories${buildQuery(params as Record<string, unknown>)}`,
      { method: 'GET' },
      MemoryListResponseSchema,
    )
  },

  approveMemory(id: string): Promise<MemoryDecisionResponse> {
    return request(
      `/api/memories/${encodeURIComponent(id)}/approve`,
      { method: 'POST' },
      MemoryDecisionResponseSchema,
    )
  },

  rejectMemory(id: string): Promise<MemoryDecisionResponse> {
    return request(
      `/api/memories/${encodeURIComponent(id)}/reject`,
      { method: 'POST' },
      MemoryDecisionResponseSchema,
    )
  },
}

export type ListMemoriesParams = {
  status: MemoryStatus
  project?: string
  limit?: number
}

export type { Job, JobEvent, CreateJobRequest, Memory, MemoryStatus }
